import React from 'react';
import classNames from 'classnames';
import styles from './UpgradePromptButton.less';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { RouterLink } from 'app/src/components/RouterLink';
import { dontUpsell } from '@trello/browser';

/**
 * UpgradePromptButton props
 *
 * cta: call to action of prompt, navigates user within the page
 * ctaLink: link for call to action
 * ctaOnClick: function expected to fire side-effects like a trackUe event,
 *             fired on click of cta
 * icon: the icon to render
 * theme: controls theme
 * justify: controls justification of button text
 */

type Theme = 'light' | 'dark' | 'gradient';
type Justify = 'left' | 'center';

interface UpgradePromptButtonProps {
  cta: string;
  ctaLink: string;
  ctaOnClick?: () => void;
  icon?: JSX.Element;
  theme?: Theme;
  justify?: Justify;
  shouldFitContainer?: boolean;
  openInNewTab?: boolean;
  allowUpsell?: boolean;
  testId?: string;
}

const getTheme = (theme?: Theme) => {
  switch (theme) {
    case 'dark':
      return styles.upgradePromptButtonDarkTheme;
    case 'light':
      return styles.upgradePromptButtonLightTheme;
    case 'gradient':
      return styles.upgradePromptButtonGradientTheme;
    default:
      return styles.upgradePromptButtonLightTheme;
  }
};

const getJustification = (justify?: Justify) => {
  switch (justify) {
    case 'left':
      return styles.upgradePromptJustifyLeft;
    case 'center':
      return styles.upgradePromptJustifyCenter;
    default:
      return styles.upgradePromptJustifyCenter;
  }
};

export const UpgradePromptButton: React.FunctionComponent<UpgradePromptButtonProps> = ({
  cta,
  ctaLink,
  ctaOnClick,
  icon,
  theme,
  justify,
  shouldFitContainer,
  openInNewTab,
  allowUpsell,
  testId,
}) => {
  if (dontUpsell() && !allowUpsell) {
    return null;
  }

  const onClickCTA = () => {
    if (ctaOnClick) {
      ctaOnClick();
    }
  };

  const promptClasses = classNames(
    styles.upgradePromptButton,
    getTheme(theme),
    shouldFitContainer && styles.upgradePromptFitContainer,
    getJustification(justify),
  );

  let ClickableComponent = ctaLink === '/business-class' ? 'a' : RouterLink;

  if (!ctaLink) {
    ClickableComponent = 'button';
  }

  return (
    <ClickableComponent
      data-test-id={testId}
      href={ctaLink}
      className={promptClasses}
      // eslint-disable-next-line react/jsx-no-bind
      onClick={onClickCTA}
      {...(openInNewTab ? { target: '_blank' } : {})}
    >
      {icon !== undefined && (
        <div className={styles.upgradePromptButtonIconBackground}>
          {React.cloneElement(icon, {
            dangerous_className: styles.upgradePromptButtonIcon,
            size: 'small',
          })}
        </div>
      )}
      <div className={styles.upgradePromptButtonCTA}>{cta}</div>
    </ClickableComponent>
  );
};

export const UpgradePromptButtonConnected: React.FunctionComponent<UpgradePromptButtonProps> = (
  props,
) => (
  <ComponentWrapper>
    <UpgradePromptButton {...props} />
  </ComponentWrapper>
);
